import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { 
  Save, Eye, EyeOff, Upload, RotateCcw, History, 
  Check, AlertTriangle, Clock, FileText, ChevronLeft,
  Loader2, Bold, Italic, Heading, List, Link as LinkIcon
} from 'lucide-react';
import { toast } from 'sonner';

const LegalEditor = ({ pageType, onClose }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [reverting, setReverting] = useState(false);
  
  // Content state
  const [title, setTitle] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [publishedContent, setPublishedContent] = useState('');
  const [versionId, setVersionId] = useState('v1.0');
  const [publishedAt, setPublishedAt] = useState(null);
  const [draftUpdatedAt, setDraftUpdatedAt] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isDefault, setIsDefault] = useState(true);
  
  // UI state
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('editor');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // P0: Re-acceptance checkbox state
  const [requireReacceptance, setRequireReacceptance] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  
  // Auto-save debounce
  const autoSaveTimeoutRef = useRef(null);
  const lastSavedContentRef = useRef('');
  
  const pageLabels = {
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    insurance: 'Insurance Information',
    'community-guidelines': 'Community Guidelines',
    'trust-safety': 'Trust & Safety',
    'renter-agreement': 'Renter Agreement'
  };
  
  // P0: Only terms and privacy can require re-acceptance
  const canRequireReacceptance = pageType === 'terms' || pageType === 'privacy';
  
  // Fetch page content on mount
  useEffect(() => {
    fetchPageContent();
    // eslint-disable-next-line
  }, [pageType]);
  
  const fetchPageContent = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/platform-owner/legal/${pageType}`);
      setTitle(res.data.title || '');
      setDraftContent(res.data.draft_content || '');
      setPublishedContent(res.data.published_content || '');
      setVersionId(res.data.version_id || 'v1.0');
      setPublishedAt(res.data.published_at);
      setDraftUpdatedAt(res.data.draft_updated_at);
      setIsDirty(res.data.is_dirty || false);
      setIsDefault(res.data.is_default || false);
      lastSavedContentRef.current = res.data.draft_content || '';
    } catch (error) {
      console.error('Error fetching page:', error);
      toast.error('Failed to load legal page');
    } finally {
      setLoading(false);
    }
  };
  
  // Auto-save with debounce
  const saveDraft = useCallback(async (content, titleValue) => {
    if (content === lastSavedContentRef.current) return;
    
    setSaving(true);
    try {
      await api.put(`/platform-owner/legal/${pageType}/draft`, {
        title: titleValue,
        content: content
      });
      lastSavedContentRef.current = content;
      setDraftUpdatedAt(new Date().toISOString());
      setIsDirty(content !== publishedContent);
    } catch (error) {
      console.error('Error saving draft:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save draft. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  }, [api, pageType, publishedContent]);
  
  // Debounced auto-save on content change
  useEffect(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    if (draftContent && draftContent !== lastSavedContentRef.current) {
      autoSaveTimeoutRef.current = setTimeout(() => {
        saveDraft(draftContent, title);
      }, 2000); // 2 second debounce
    }
    
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [draftContent, title, saveDraft]);
  
  const handlePublish = async () => {
    if (!draftContent.trim()) {
      toast.error('Cannot publish empty content');
      return;
    }
    
    // P0: If re-acceptance is selected, show confirmation first
    if (canRequireReacceptance && requireReacceptance && !showPublishConfirm) {
      setShowPublishConfirm(true);
      return;
    }
    
    // First ensure draft is saved
    if (draftContent !== lastSavedContentRef.current) {
      await saveDraft(draftContent, title);
    }
    
    setPublishing(true);
    setShowPublishConfirm(false);
    try {
      const res = await api.post(`/platform-owner/legal/${pageType}/publish`, {
        require_reacceptance: canRequireReacceptance ? requireReacceptance : false
      });
      setVersionId(res.data.version_id);
      setPublishedAt(res.data.published_at);
      setPublishedContent(draftContent);
      setIsDirty(false);
      setIsDefault(false);
      setRequireReacceptance(false); // Reset checkbox after publish
      
      let successMsg = `${pageLabels[pageType]} published as ${res.data.version_id}`;
      if (res.data.requires_reacceptance && res.data.notifications_sent > 0) {
        successMsg += `. ${res.data.notifications_sent} users notified to re-accept.`;
      }
      toast.success(successMsg);
    } catch (error) {
      console.error('Error publishing:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to publish. Please try again.';
      toast.error(errorMessage);
    } finally {
      setPublishing(false);
    }
  };
  
  const handleRevert = async () => {
    if (!window.confirm('Revert draft to the currently published version? Unsaved changes will be lost.')) {
      return;
    }
    
    setReverting(true);
    try {
      await api.post(`/platform-owner/legal/${pageType}/revert`);
      setDraftContent(publishedContent);
      lastSavedContentRef.current = publishedContent;
      setIsDirty(false);
      toast.success('Draft reverted to published version');
    } catch (error) {
      console.error('Error reverting:', error);
      toast.error(error.response?.data?.detail || 'Failed to revert');
    } finally {
      setReverting(false);
    }
  };
  
  const fetchVersionHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get(`/platform-owner/legal/${pageType}/history`);
      setVersionHistory(res.data.versions || []);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Failed to load version history');
    } finally {
      setHistoryLoading(false);
    }
  };
  
  const handleRestoreVersion = async (versionIdToRestore) => {
    if (!window.confirm(`Restore version ${versionIdToRestore} to draft? You can then publish it to make it live.`)) {
      return;
    }
    
    try {
      await api.post(`/platform-owner/legal/${pageType}/restore/${versionIdToRestore}`);
      await fetchPageContent();
      setShowHistoryModal(false);
      toast.success(`Version ${versionIdToRestore} restored to draft`);
    } catch (error) {
      console.error('Error restoring version:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore version');
    }
  };
  
  // Manual save button
  const handleManualSave = async () => {
    await saveDraft(draftContent, title);
    toast.success('Draft saved');
  };
  
  // Markdown toolbar helpers
  const insertMarkdown = (before, after = '') => {
    const textarea = document.getElementById('legal-content-editor');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = draftContent.substring(start, end);
    const newText = draftContent.substring(0, start) + before + selectedText + after + draftContent.substring(end);
    
    setDraftContent(newText);
    
    // Set cursor position after insert
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, end + before.length);
    }, 0);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-white/70 hover:text-white"
            data-testid="back-button"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-400" />
              {pageLabels[pageType]}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <Badge className="bg-purple-500/20 text-purple-400 text-xs" data-testid="version-badge">
                {versionId}
              </Badge>
              {publishedAt && (
                <span className="text-xs text-white/50">
                  Published: {new Date(publishedAt).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          {saving && (
            <span className="text-xs text-white/50 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              Saving...
            </span>
          )}
          {!saving && draftUpdatedAt && (
            <span className="text-xs text-white/50 flex items-center gap-1">
              <Check className="w-3 h-3 text-green-400" />
              Draft saved
            </span>
          )}
          {isDirty && (
            <Badge className="bg-amber-500/20 text-amber-400 text-xs" data-testid="unsaved-badge">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Unpublished Changes
            </Badge>
          )}
        </div>
      </div>
      
      {/* Title Input */}
      <div>
        <label className="text-sm text-white/70 mb-1 block">Page Title</label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Page title..."
          className="bg-white/10 border-white/20 text-white h-10"
          data-testid="title-input"
        />
      </div>
      
      {/* Editor Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
          <TabsList className="bg-white/5 border border-white/10 p-1">
            <TabsTrigger value="editor" className="data-[state=active]:bg-purple-600 text-xs sm:text-sm">
              <FileText className="w-4 h-4 mr-1" />
              Editor
            </TabsTrigger>
            <TabsTrigger value="preview" className="data-[state=active]:bg-purple-600 text-xs sm:text-sm">
              <Eye className="w-4 h-4 mr-1" />
              Preview
            </TabsTrigger>
            <TabsTrigger value="published" className="data-[state=active]:bg-purple-600 text-xs sm:text-sm">
              <Check className="w-4 h-4 mr-1" />
              Live Version
            </TabsTrigger>
          </TabsList>
          
          {/* Markdown Toolbar */}
          {activeTab === 'editor' && (
            <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => insertMarkdown('**', '**')}
                className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                title="Bold"
              >
                <Bold className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => insertMarkdown('*', '*')}
                className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                title="Italic"
              >
                <Italic className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => insertMarkdown('## ')}
                className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                title="Heading"
              >
                <Heading className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => insertMarkdown('- ')}
                className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                title="List"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => insertMarkdown('[', '](url)')}
                className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10"
                title="Link"
              >
                <LinkIcon className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
        
        <TabsContent value="editor" className="mt-0">
          <Textarea
            id="legal-content-editor"
            value={draftContent}
            onChange={(e) => setDraftContent(e.target.value)}
            placeholder="Write your content in Markdown format..."
            className="min-h-[400px] sm:min-h-[500px] bg-white/5 border-white/20 text-white font-mono text-sm leading-relaxed resize-y"
            data-testid="content-editor"
          />
          <p className="text-xs text-white/40 mt-2">
            Supports Markdown: **bold**, *italic*, ## headings, - lists, [links](url)
          </p>
        </TabsContent>
        
        <TabsContent value="preview" className="mt-0">
          <Card className="bg-white/5 border-white/10 min-h-[400px] sm:min-h-[500px]">
            <CardHeader className="pb-2 border-b border-white/10">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm text-white/70">Draft Preview</CardTitle>
                <Badge className="bg-amber-500/20 text-amber-400 text-xs">DRAFT</Badge>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="prose prose-invert prose-sm sm:prose-base max-w-none legal-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {draftContent || '*No content yet*'}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="published" className="mt-0">
          <Card className="bg-white/5 border-white/10 min-h-[400px] sm:min-h-[500px]">
            <CardHeader className="pb-2 border-b border-white/10">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm text-white/70">Currently Live ({versionId})</CardTitle>
                <Badge className="bg-green-500/20 text-green-400 text-xs">LIVE</Badge>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="prose prose-invert prose-sm sm:prose-base max-w-none legal-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {publishedContent || '*No published content yet*'}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 border-t border-white/10">
        <div className="flex items-center gap-2">
          <Button
            onClick={handleManualSave}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            disabled={saving}
            data-testid="save-draft-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            Save Draft
          </Button>
          <Button
            onClick={handleRevert}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            disabled={reverting || !isDirty}
            data-testid="revert-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Revert
          </Button>
          <Button
            onClick={() => { fetchVersionHistory(); setShowHistoryModal(true); }}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="history-btn"
          >
            <History className="w-4 h-4 mr-2" />
            History
          </Button>
        </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* P0: Re-acceptance checkbox (only for terms and privacy) */}
          {canRequireReacceptance && (
            <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
              <Checkbox
                id="require-reacceptance"
                checked={requireReacceptance}
                onCheckedChange={setRequireReacceptance}
                className="border-amber-500/50 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                data-testid="require-reacceptance-checkbox"
              />
              <label 
                htmlFor="require-reacceptance" 
                className="text-xs text-amber-200 cursor-pointer select-none"
              >
                Require all users to re-accept on publish
              </label>
            </div>
          )}
          
          <Button
            onClick={handlePublish}
            className={`${requireReacceptance ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'} text-white`}
            disabled={publishing || !draftContent.trim()}
            data-testid="publish-btn"
          >
            {publishing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            {requireReacceptance ? 'Publish & Notify Users' : 'Publish to Live'}
          </Button>
        </div>
      </div>
      
      {/* P0: Publish Confirmation Modal (when re-acceptance is required) */}
      <Dialog open={showPublishConfirm} onOpenChange={setShowPublishConfirm}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <AlertTriangle className="w-5 h-5" />
              Confirm Re-Acceptance Requirement
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-white/80">
              You are about to publish <strong>{pageLabels[pageType]}</strong> with <strong>mandatory re-acceptance</strong> enabled.
            </p>
            
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-sm">
              <p className="text-amber-200 font-medium mb-2">This will:</p>
              <ul className="space-y-1 text-amber-200/80 list-disc list-inside">
                <li>Send notifications to all users</li>
                <li>Block app access until users re-accept</li>
                <li>Block new bookings until terms accepted</li>
              </ul>
            </div>
            
            <p className="text-xs text-white/50">
              This action is immediate and affects all platform users.
            </p>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPublishConfirm(false)}
              className="border-white/20 text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePublish}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              disabled={publishing}
              data-testid="confirm-publish-reacceptance-btn"
            >
              {publishing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Confirm & Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Version History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-purple-400" />
              Version History - {pageLabels[pageType]}
            </DialogTitle>
          </DialogHeader>
          
          {historyLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : versionHistory.length === 0 ? (
            <p className="text-center py-8 text-white/60">No version history available</p>
          ) : (
            <div className="space-y-3">
              {versionHistory.map((version, idx) => (
                <div 
                  key={idx}
                  className={`p-4 rounded-lg border ${
                    version.is_current 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : 'bg-white/5 border-white/10'
                  }`}
                  data-testid={`version-${version.version_id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge className={version.is_current ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/70'}>
                        {version.version_id}
                      </Badge>
                      {version.is_current && (
                        <Badge className="bg-green-500/20 text-green-400 text-xs">CURRENT</Badge>
                      )}
                    </div>
                    {!version.is_current && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRestoreVersion(version.version_id)}
                        className="border-white/20 text-white hover:bg-white/10 h-8 text-xs"
                      >
                        <RotateCcw className="w-3 h-3 mr-1" />
                        Restore
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-white/50">
                    {version.published_at 
                      ? `Published: ${new Date(version.published_at).toLocaleString()}`
                      : 'Not published'}
                  </p>
                  <p className="text-sm text-white/70 mt-2 line-clamp-3">
                    {version.content?.substring(0, 200)}...
                  </p>
                </div>
              ))}
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowHistoryModal(false)}
              className="border-white/20 text-white"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LegalEditor;
